import _ from 'lodash';
import { BBModel, CardData, StoryModel, StoryVersionType } from 'types/story';
import produce, { Draft } from 'immer';
import { appLog } from 'utils/helpers';
import facades from 'utils/facades/card-facades';
import { StoryFacade } from 'utils/facades/story-facade';
import { CARD_DEFAULT_TEMPLATE_NAME, COMPONENT_STATES } from 'common/constants';
import { INTEGRATIONS_FIELD, INTEGRATIONS_NAMES } from 'utils/facades/integrations-facade';
// TODO: maybe need to move this config to common, to avoid import of admin to client
import { platformsConfig } from 'admin/utils/story-media-platforms-config';
import { componentWalk } from 'utils/component-walk';
import { AnimationData } from 'client/components/common/BuildingBlocks/animation';

const log = appLog.extend('utils:StoryMigrationAdapter');

type VoluntaryUpdate = {
	ver: string; // story version when changed was published
	timestamp: string;
	desc: string; // description of the update
	apply: () => StoryMigrationAdapter; // function to apply story update
	status: () => boolean; // is updated applied or not. (true: applied)
};

export type VoluntaryUpdateList = VoluntaryUpdate[];

const LS_SKIP_UPDATE = 'sc-update-skip';

/**
 * @info Migration currently in only supported for a latest version of story only.
 */
export class StoryMigrationAdapter {
	story: StoryModel;

	constructor({ story }: { story: StoryModel }) {
		this.story = story;
	}

	get voluntaryUpdateList(): VoluntaryUpdateList {
		return [
			{
				ver: '1.5.0',
				timestamp: '2021-08-17T14:00:00.000Z',
				desc:
					'Added "Full HD" platform.' +
					'\n • "Full HD" - 1920px a wider' +
					'\n • "Desktop" - 1280px a wider' +
					'\n • "Tablet" - 1279px - 768px' +
					'\n • "Mobile" - 767px and less',
				apply: () => {
					this.story = produce(this.story, draft => {
						const { mediaQuery } = draft.storyVersions[StoryFacade.VERSIONS.latest].data;
						mediaQuery.config = platformsConfig[mediaQuery.defaultPlatform];
					});
					return this;
				},
				status: () => !!this.story.storyVersions[StoryFacade.VERSIONS.latest].data.mediaQuery.config.fullHD,
			},
			{
				ver: '1.5.27',
				timestamp: '2021-10-25T14:00:00.000Z',
				desc:
					'Added "Mobile landscape" platform.' +
					'\n • "Mobile landscape" - 767px and less.' +
					'\n • "Mobile" - 478px and less.',
				apply: () => {
					this.story = produce(this.story, draft => {
						const { mediaQuery } = draft.storyVersions[StoryFacade.VERSIONS.latest].data;
						mediaQuery.config = platformsConfig[mediaQuery.defaultPlatform];
					});
					return this;
				},
				status: () =>
					!!this.story.storyVersions[StoryFacade.VERSIONS.latest].data.mediaQuery.config.mobileLandscape,
			},
		];
	}

	get storyVoluntaryUpdateList() {
		return this.voluntaryUpdateList.filter(o => !o.status());
	}

	skipUpdate(updates: VoluntaryUpdateList) {
		localStorage.setItem(
			LS_SKIP_UPDATE,
			JSON.stringify(Object.assign(StoryMigrationAdapter.lsSkipItem, { [this.story.id]: updates[0].ver }))
		);
	}

	isShowUpdate(updates: VoluntaryUpdateList) {
		return updates.length > 0 && StoryMigrationAdapter.lsSkipItem?.[this.story.id] !== updates[0].ver;
	}

	private static get lsSkipItem() {
		return JSON.parse(localStorage.getItem(LS_SKIP_UPDATE) ?? '{}');
	}

	/**
	 * From version 1.2.0 all story symbols were moved from "story.settings.symbols" to "story.data.symbols"
	 */
	private moveSymbols(draft: Draft<StoryVersionType>) {
		log('moveSymbols:START');

		const oldPath = 'settings.symbols';
		const oldSymbols = _.get(draft, oldPath);

		if (oldSymbols) {
			log('moveSymbols:ADAPT', draft.storyId);
			// eslint-disable-next-line no-param-reassign
			draft.data.symbols = { ...oldSymbols };

			_.unset(draft, oldPath);
		}

		log('moveSymbols:COMPLETE');

		return this;
	}

	/**
	 * From version 1.8.0 all story integrations moved to "story.settings.integrations"
	 * Old ones: "story.settings.ga", "story.settings.ga4", "story.settings.headScript", "story.settings.bodyScript"
	 * @private
	 */
	private moveStoryIntegrations(draft: Draft<StoryVersionType>) {
		[
			['ga', `${INTEGRATIONS_NAMES.GA}.${INTEGRATIONS_FIELD.GA_UNIVERSAL}`],
			['ga4', `${INTEGRATIONS_NAMES.GA}.${INTEGRATIONS_FIELD.GA4}`],
			['headScript', `${INTEGRATIONS_NAMES.SCRIPT}.${INTEGRATIONS_FIELD.HEAD_SCRIPT}`],
			['bodyScript', `${INTEGRATIONS_NAMES.SCRIPT}.${INTEGRATIONS_FIELD.BODY_SCRIPT}`],
		].forEach(([oldPath, newPath]) => {
			if (_.has(draft.settings, oldPath)) {
				log('moveStoryIntegrations:ADAPT', oldPath);
				// eslint-disable-next-line no-param-reassign
				if (!draft.settings.integrations) draft.settings.integrations = {};

				// set old value at new path
				_.set(draft.settings.integrations, newPath, _.get(draft.settings, oldPath));

				// remove old field
				_.unset(draft.settings, oldPath);
			}
		});
	}

	/**
	 * Card settings were changed many times but must always contain latest configurations
	 */
	private updateCardSettings(card: CardData) {
		log('updateCardSettings:START', card._id);

		if (!facades[card.type]) {
			log('updateCardSettings:COMPLETE');
			return this;
		}

		const template = facades[card.type].template[CARD_DEFAULT_TEMPLATE_NAME]();

		// eslint-disable-next-line no-param-reassign
		card.settings = { ...(card?.settings || {}), ...template.editor.card.settings };

		log('updateCardSettings:COMPLETE', card._id);

		return this;
	}

	/**
	 * Loop through blocks and update theirs data
	 */
	private updateBuildingBlocks(elements: BBModel[]) {
		log('updateBuildingBlocks:START');

		componentWalk<BBModel>(elements, function walk({ component, path, index }) {
			/* [UPDATE BUILDING BLOCK DATA] - Start */
			if (!component) return;

			// In v1.5.1 was introduced new animation settings UI/UX and new features. AnimationData object was changed.
			animationCheck(component);

			componentWalk(component.children, walk);
			/* [UPDATE BUILDING BLOCK DATA] - End */
		});

		log('updateBuildingBlocks:COMPLETE');

		return this;
	}

	/**
	 * Forced migration
	 */
	adapt() {
		const t0 = performance.now();
		const version = this.story.storyVersions[StoryFacade.VERSIONS.latest];
		const { appVersion } = this.story.storyVersions[StoryFacade.VERSIONS.latest].data;

		if (!version || (appVersion && appVersion === process.env.VERSION)) {
			return this.story;
		}

		log('adapt:START');

		// change symbol path
		this.moveSymbols(version);

		// change integrations path
		this.moveStoryIntegrations(version);

		// update global blocks
		this.updateBuildingBlocks(version.data.elements);

		// update symbol blocks
		const symbols: BBModel[] = [];
		Object.values(version.data.symbols || {}).forEach(symbol => {
			symbols.push(symbol.master);
			Object.values(symbol.instance).forEach(instance => {
				symbols.push(instance);
			});
		});
		this.updateBuildingBlocks(symbols);

		version.data?.steps?.forEach(step => {
			step.cards.forEach(card => {
				// Update
				this.updateCardSettings(card);

				// Update
				this.updateBuildingBlocks(card.elements);
			});
		});

		log('adapt:COMPLETE', `${(performance.now() - t0).toFixed(4)}ms`);

		return this.story;
	}
}

function animationCheck(component: BBModel) {
	const animationRoot = component.uiConfig?.componentProps?.animation?.[COMPONENT_STATES.DEFAULT];

	if (!animationRoot) return;

	Object.keys(animationRoot).forEach((platform, i, arr) => {
		const animationData: Draft<Record<string, AnimationData> | AnimationData> = animationRoot[platform];

		/*
			 Update animation:
			 Old format: animationData = AnimationData;
			 New format: animationData = Record<string, AnimationData>
		 */
		if (animationData.keyframes || animationData.trigger) {
			log('animationCheck:ADAPT');

			if ((animationData.keyframes as any)?.initial && (animationData.keyframes as any)?.final) {
				animationRoot[platform] = updateAnimation(
					animationData as AnimationData,
					`${component.type} - ${component._id.slice(0, 4)}`
				);
			} else {
				delete animationRoot[platform];
			}
		}
	});
}

function updateAnimation(draft: Draft<AnimationData>, name: string) {
	const id = 'migration';

	return {
		[id]: {
			...draft,
			id,
			name,
			delay: '0',
			loop: 'false',
			reverse: 'false',
			keyframes: {
				'0': updateKeyframe(draft.keyframes.initial, { position: '0', name, id, ease: draft.ease }),
				'100': updateKeyframe(draft.keyframes.final, { position: '100', name, id, ease: draft.ease }),
			},
		},
	};
}

function updateKeyframe(
	keyframe: AnimationData['keyframes'][string],
	params: { position: string; name: string; id: string; ease: string }
) {
	return {
		position: params.position,
		event: params.position === '0' ? '' : params.name,
		eventId: params.position === '0' ? '' : params.id,
		props: Object.values(keyframe.props).reduce((acc, prop) => {
			acc[prop.name.toUpperCase()] = {
				...prop,
				name: prop.name.toUpperCase(),
				ease: prop.ease ? prop.ease : params.ease,
			};
			return acc;
		}, {}),
	};
}
